import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { AlertTriangle, CreditCard, Mail, RefreshCw } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialExpired, setTrialExpired] = useState(null);
  const [pendingActivation, setPendingActivation] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    setTrialExpired(null);
    setPendingActivation(null);
    const result = await onLogin(email, password);
    
    if (result.success) {
      toast.success('Bienvenido a Proyecto Residente!');
    } else {
      if (result.trialExpired) {
        if (result.trialExpired.code === 'pending_activation') {
          setPendingActivation(result.trialExpired);
        } else {
          setTrialExpired(result.trialExpired);
        }
      } else {
        toast.error(result.message);
      }
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!trialExpired?.user_id) return;
    setPayLoading(true);
    try {
      const paymentResponse = await axios.post(`${API_BASE_URL}/api/payments/create-preference`, {
        user_id: trialExpired.user_id
      });
      const { init_point } = paymentResponse.data;
      if (init_point) {
        window.location.href = init_point;
        return;
      }
      toast.error('Error al crear el pago.');
    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.response?.data?.detail || 'Error al conectar con MercadoPago.');
    }
    setPayLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-white">
      <div className="w-full max-w-md">
        <div className="rounded-2xl p-8 animate-fadeIn bg-white">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png"
              alt="Proyecto Residente"
              className="w-[80rem] max-w-full h-auto"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-black mb-2">
              Iniciar Sesion
            </h2>
          </div>

          {/* Pending Activation Banner (paid but not activated from email) */}
          {pendingActivation && (
            <div className="mb-6 p-5 bg-green-50 border border-green-200 rounded-xl" data-testid="pending-activation-banner">
              <div className="flex items-start gap-3 mb-3">
                <Mail size={24} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-green-900 text-base">
                    Pago recibido - Activa tu cuenta
                  </h3>
                  <p className="text-green-700 text-sm mt-1">
                    Para entrar, debes activar tu cuenta desde el correo que te enviamos. Revisa tu bandeja de entrada y spam.
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setResending(true);
                  try {
                    await axios.post(`${API_BASE_URL}/api/auth/resend-activation/${pendingActivation.user_id}`);
                    setResendDone(true);
                    toast.success('Correo reenviado');
                  } catch (err) {
                    toast.error('Error al reenviar el correo');
                  }
                  setResending(false);
                }}
                disabled={resending || resendDone}
                className="w-full mt-3 border border-green-300 text-green-800 font-medium py-2.5 rounded-full hover:bg-green-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="resend-activation-login-btn"
              >
                {resending ? (
                  <><RefreshCw size={16} className="animate-spin" /> Reenviando...</>
                ) : resendDone ? (
                  'Correo reenviado'
                ) : (
                  <><RefreshCw size={16} /> No me llego, reenviar correo</>
                )}
              </button>
            </div>
          )}

          {/* Trial Expired Banner */}
          {trialExpired && (
            <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl" data-testid="trial-expired-banner">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-amber-900 text-base">
                    {trialExpired.code === 'subscription_expired'
                      ? 'Tu suscripcion ha expirado'
                      : 'Tu version de prueba ha caducado'}
                  </h3>
                  <p className="text-amber-700 text-sm mt-1">
                    {trialExpired.code === 'subscription_expired'
                      ? 'Debes renovar tu suscripcion para seguir usando la app.'
                      : 'Tus 72 horas de prueba gratuita terminaron. Paga para obtener acceso completo por 6 meses.'}
                  </p>
                </div>
              </div>
              <button
                onClick={handlePayNow}
                disabled={payLoading}
                data-testid="pay-to-continue-btn"
                className="w-full mt-3 bg-black text-white font-semibold py-3.5 rounded-full text-base hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
              >
                {payLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Redirigiendo a MercadoPago...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Pagar y seguir usando la app
                  </>
                )}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo Electronico</label>
              <input
                type="email"
                data-testid="login-email-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contrasena</label>
              <input
                type="password"
                data-testid="login-password-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              className="w-full py-3 px-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Iniciando sesion...
                </>
              ) : (
                'Iniciar Sesion'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              No tienes cuenta?{' '}
              <Link 
                to="/register" 
                data-testid="register-link"
                className="text-black hover:text-gray-600 font-semibold transition-colors"
              >
                Registrate aqui
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
